import { BCMSCurrency, BrandingStyle, DisabledPrompt, useBCMSModal, useStateManager } from "@bcm-dev/bcms-vite";
import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from "components/custom-ui/CustomAccordion";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import DateSelect from 'components/custom-ui/DateSelect';
import { FormContainer, FormRowView } from 'components/shared';
import IconToggle from "components/shared/views/IconToggle";
import { PrimaryButton, SecondaryButton, UIAmountInput, UITextInput } from 'components/ui';
import { SVGAsset } from "res";

/**
 * @function TASPositivePayViewChecksMobileView
 * @return {React.Component}
 * @exported true
 * @description The TAS Positive Pay Add Check mobile content view.
 */
export default function TASPositivePayViewChecksMobileView(props) {
    const { data, ui, onCancelClick, onSaveClick } = useStateManager();
    const checkForms = data.checkForms;
    const checkFormsUI = ui.checkForms;
    const userEditedCheckForms = data.search.userEditedCheckForms().length > 0;
    const { open } = useBCMSModal();

    const rows = checkForms.reduce((acc, item, index) => {
        const amount = item.amount ? item.amount.amount : undefined;
        const isEditing = checkFormsUI[index].isEditing;
        const editItem = data.search.editCheckForms.filter(_item => _item.id === item.id)[0];
        
        acc.push(
            isEditing
            ? <EditingRow key={ item.id } amount={ amount  } item={ item } editItem={ editItem }/>
            : <Row key={ item.id } amount={ amount  } item={ item }/>
        );
        return acc;
    }, []);

    const onYesClick = evt => {
        onCancelClick();
    }

    const openCancelModal = val => {
        open("TASPositivePayCancelEditChangeModal", { onYesClick: onYesClick } );
    };
    
    return (
        <>
        { rows }
        { userEditedCheckForms &&
        <div className="flex-container flex-horizontal button-container search-results-button-container">
            <SecondaryButton className="set-width" text="Cancel" onClick={ openCancelModal } />
            <PrimaryButton className="set-width" text="Save" data-point="Search_Button" onClick={ onSaveClick } />
        </div>
        }
        </>
    );
}

/**
 * @function Row
 * @return {React.Component}
 * @exported false
 * @description A row that can only be read by the user.
 */
function Row(props) {
  const { ui, onChange, onEditClick, onMobileCloseButtonClick } = useStateManager();
  const item = props.item ?? {};
  const uiItem = ui.checkForms.filter(_item => _item.id === item.id)[0];
  const amount = props.amount ?? "";
  const amountFormatted = BCMSCurrency.init(amount).format();
  const chevronIconClosedClass = uiItem.mobile.isActive ? "" : "closed";
  const account = item.account;
  const canDelete = item.canDelete;

  return (
    <div key={ item.id } className="tas-positive-pay-view-checks-mobile-view show-on-mobile">
        <div className="flex-container align-top">
            <CustomAccordion isOpen={ uiItem.mobile.isActive }>
                <div className="flex-container flex-horizontal align-top check-custom-accordion-content">
                        <div className="set-width">
                            <CustomAccordionToggle className={ "" } onClick={ onMobileCloseButtonClick(item.id) }>
                                <IconToggle className={ `chevron-icon-container ${ chevronIconClosedClass }`} icon="DownChevronIcon" text=""/>
                            </CustomAccordionToggle>
                        </div>
                        <div className="flex-width">
                            <div className="flex-container flex-vertical check-details">
                                <div className="check-detail-row">
                                    <span className="sub-text">Account: </span>
                                    <b>{ account.nickname } - { account.number }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Issued Date: </span>
                                    <b>{ item.issuedDate.format("m/d/Y") }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Check Number: </span>
                                    <b>{ item.checkNumber }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Amount: </span>
                                    <b>{ amountFormatted }</b>
                                </div>
                            </div>
                        </div>
                </div>
                <CustomAccordionContent className="flex-container flex-vertical">
                    <CustomAccordionToggle className="flex-container flex-width close-accordion accordion-edit-view" onClick={ onMobileCloseButtonClick(item.id) }>
                        <BrandingStyle className="flex-width sub-text" element={<p >DETAILS</p>}/>
                        <SVGAsset className="icon set-width" id="CloseX" data-point="CloseButton" />
                    </CustomAccordionToggle>
                    <FormContainer className="small-form-width">
                        
                        <FormRowView className="sub-text" text="Account">
                            <b>{ `${ item.account.name } ${ item.account.number }` }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Issued Date">
                            <b>{ item.issuedDate.format("m/d/Y") }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Check Number">
                            <b>{ item.checkNumber }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Amount">
                            <b>{ amountFormatted }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Payee">
                            <b>{ item.payee }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Reference Number">
                            <b>{ item.referenceNumber }</b>
                        </FormRowView>
                        <FormRowView className="sub-text type" text="Type">
                            <b>{ item.type }</b>
                        </FormRowView>
                        <FormRowView className="sub-text type type-form-view" text="Status">
                            <b>{ item.status }</b>
                        </FormRowView>
                        <div className="flex-container flex-vertical">
                            <a className="clickable edit-link" onClick={ onEditClick(item.id) }>Edit</a>
                            { canDelete && 
                            <DisabledPrompt>
                                <a className="clickable">Delete</a>
                            </DisabledPrompt>
                            }
                        </div>
                    </FormContainer>
                </CustomAccordionContent>
            </CustomAccordion>
        </div>
    </div>
  );
}

/**
 * @function EditingRow
 * @return {React.Component}
 * @exported false
 * @description A row that can be edited by the user.
 */
function EditingRow(props) {
  const { data, ui, onEdit, onMobileCloseButtonClick, onMobileCancelClick, onMobileSaveClick } = useStateManager();
  const item = props.editItem ?? {};
  const uiItem = ui.checkForms.filter(_item => _item.id === item.id)[0];
  const amount = props.amount ?? "";
  const amountFormatted = BCMSCurrency.init(amount).format();
  const search = data.search;
  const accountItems = search.accounts.reduce((acc, item) => {
      acc.push(
          <CustomSelectItem key={ item.id } value={ item.id }>{ item.name }</CustomSelectItem>
      )
      return acc;
  }, []);
  const chevronIconClosedClass = uiItem.mobile.isActive ? "" : "closed";
  const account = item.account;

  return (
    <div key={ item.id } className="tas-positive-pay-view-checks-mobile-view show-on-mobile">
        <div className="flex-container align-top">
            <CustomAccordion isOpen={ uiItem.mobile.isActive }>
                <div className="flex-container flex-horizontal align-top check-custom-accordion-content">
                        <div className="set-width">
                            <CustomAccordionToggle className={ "" } onClick={ onMobileCloseButtonClick(item.id) }>
                                <IconToggle className={ `chevron-icon-container ${ chevronIconClosedClass }`} icon="DownChevronIcon" text=""/>
                            </CustomAccordionToggle>
                        </div>
                        <div className="flex-width">
                            <div className="flex-container flex-vertical check-details">
                                <div className="check-detail-row">
                                    <span className="sub-text">Account: </span>
                                    <b>{ account.nickname } - { account.number }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Issued Date: </span>
                                    <b>{ item.issuedDate.format("m/d/Y") }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Check Number: </span>
                                    <b>{ item.checkNumber }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Amount: </span>
                                    <b>{ amountFormatted }</b>
                                </div>
                            </div>
                        </div>
                </div>
                <CustomAccordionContent className="flex-container flex-vertical tas-positive-pay-add-check-page-accordion-content">
                    <CustomAccordionToggle className="flex-container flex-width close-accordion accordion-edit-view" onClick={ onMobileCloseButtonClick(item.id) }>
                        <BrandingStyle className="flex-width sub-text" element={<p >EDIT CHECK</p>}/>
                        <SVGAsset className="icon set-width" id="CloseX" data-point="CloseButton" />
                    </CustomAccordionToggle>
                    <FormContainer className="small-form-width">
                        <FormRowView text="Account">
                            <CustomSelect active={ uiItem.account.isActive } value={ item.account.id } onChange={ onEdit(item.id, "account") } >
                                { accountItems }
                            </CustomSelect>
                        </FormRowView>
                        <FormRowView text="Issued Date">
                            <DateSelect active={ item.issuedDate.isActive } enablePast={ true } value={ item.issuedDate } onChange={ onEdit(item.id, "issuedDate") } />
                        </FormRowView>
                        <FormRowView text="Check Number">
                            <UITextInput value={ item.checkNumber } onChange={ onEdit(item.id, "checkNumber") } />
                        </FormRowView>
                        <FormRowView text="Amount">
                            <UIAmountInput className="amount-field" value={ amount } onChange={ onEdit(item.id, "amount") } />
                        </FormRowView>
                        <FormRowView text="Payee">
                            <UITextInput value={ item.payee } onChange={ onEdit(item.id, "payee") } />
                        </FormRowView>
                        <FormRowView text="Reference Number">
                            <UITextInput value={ item.referenceNumber } onChange={ onEdit(item.id, "referenceNumber") } />
                        </FormRowView>
                        <FormRowView className="type-form-view" text="Type">
                            <CustomSelect active={ item.type.isActive } value={ item.type } onChange={ onEdit(item.id, "type") }>
                                <CustomSelectItem value="issue">Issue</CustomSelectItem>
                                <CustomSelectItem value="stop">Stop</CustomSelectItem>
                                <CustomSelectItem value="void">Void</CustomSelectItem>
                            </CustomSelect>
                        </FormRowView>
                        <div className="flex-container flex-vertical">
                            <a className="clickable save" onClick={ onMobileSaveClick(item.id) }>Save</a>
                            <a className="clickable cancel" onClick={ onMobileCancelClick(item.id) }>Cancel</a>
                        </div>
                    </FormContainer>
                </CustomAccordionContent>
            </CustomAccordion>
        </div>
    </div>
  );
}